import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { useLocalStorage } from "primereact/hooks";

// ---------------------------------------------------------------------------
// NavLayoutContext
// ---------------------------------------------------------------------------
// Holds admin sidepanel layout state (collapsed icon-rail). Persisted to
// localStorage so the choice survives reloads. Admin-only; not shared with
// other frontends via sj-ui-toolkit.
// ---------------------------------------------------------------------------

const STORAGE_KEY = "admin_nav_collapsed";

export interface NavLayoutContextValue {
  isCollapsed: boolean;
  toggleCollapsed: () => void;
  setCollapsed: (collapsed: boolean) => void;
}

const NavLayoutContext = createContext<NavLayoutContextValue | undefined>(undefined);

export function NavLayoutProvider({ children }: { children: ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useLocalStorage<boolean>(false, STORAGE_KEY);

  const toggleCollapsed = useCallback(() => setIsCollapsed(prev => !prev), [setIsCollapsed]);
  const setCollapsed = useCallback(
    (collapsed: boolean) => setIsCollapsed(collapsed),
    [setIsCollapsed]
  );

  const value = useMemo<NavLayoutContextValue>(
    () => ({ isCollapsed, toggleCollapsed, setCollapsed }),
    [isCollapsed, toggleCollapsed, setCollapsed]
  );

  return <NavLayoutContext.Provider value={value}>{children}</NavLayoutContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNavLayout(): NavLayoutContextValue {
  const ctx = useContext(NavLayoutContext);
  if (!ctx) {
    throw new Error("useNavLayout must be used within a NavLayoutProvider");
  }
  return ctx;
}
